import { useQuery } from "@1f/react-sdk"
import { normalizeToponym } from "../helpers/stringHelpers";
import { query_base_comune_byCodBelfiore, query_base_comune_byNome, query_base_comune_byId, query_base_paese_byCodAt, query_base_paese_byIdent, query_base_paese_byNome, query_base_provincia_byIdent, query_base_provincia_byNome, query_base_stcodes_byCodBelfiore } from "../query/baseTablesQueries";

export const AddressApi = () => {
    const countryByName = useQuery( 
        query_base_paese_byNome,
        {},
        { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
    );    
    const provinceByName = useQuery(
        query_base_provincia_byNome,
        {},
        { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
    );    
    const minicipalityByName = useQuery(
        query_base_comune_byNome,
        {},
        { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
    ); 
    const provinceBySigla = useQuery(
        query_base_provincia_byIdent,
        {},
        { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
    );
    const countryByIdent = useQuery(
        query_base_paese_byIdent,
        {},
        { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
    );
    const apiBaseComuneByCodBelfiore = useQuery(
        query_base_comune_byCodBelfiore,
        {},
        { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
    );      
    const apiBasePaeseByCodAt = useQuery(
        query_base_paese_byCodAt,
        {},
        { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
    ); 
    const apiGetCapByComuneId = useQuery(
        query_base_stcodes_byCodBelfiore,
        {},
        { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
    );
    const apiBaseComuneById = useQuery(
        query_base_comune_byId,
        {},
        { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
    );

    const GetIdByPaese = async (country) => {
        try {
            let response = await countryByName.fetch({
                nome: country,
            })
            return response.data.data.base_paese[0].Id;   
        } catch (error) {
            throw new Error("Error fetching country by name");
        }
    }
    const GetIdByProvince = async (province) => {
        try {
            let response = await provinceByName.fetch({
                nome: province,
            })
            return response.data.data.base_provincia[0].Id;   
        } catch (error) {
            throw new Error("Error fetching province by name");
        }
    }
    const GetIdByMunicipality = async (municipality) => {
        try {
            let response = await minicipalityByName.fetch({
                nome: municipality,
            })
            return response.data.data.base_comune[0].Id;   
        } catch (error) {
            throw new Error("Error fetching municipality by name");
        }
    }
    const GetMunicipalityByCodeBelfiore = async (codBelfiore) => {
        try {
            return await TryGetComuneByCodBelfiore(codBelfiore);
        } catch (error) {
            throw new Error("Error fetching municipality by code belfiore");
        }
    }
    const GetPaeseByCodiceBelfiore = async (codBelfiore) => {
        try {
            const paeseResponse = await apiBasePaeseByCodAt.fetch({
                codiceAt: codBelfiore,
            });
            if(!paeseResponse.data.errors){
                return paeseResponse.data.data.base_paese[0]
            }
        } catch (error) {
            throw new Error("Error fetching country by codice belfiore");
        }
    }
    const GetCapByComuneId = async (comuneId) => {
        try {
            const capResponse = await apiGetCapByComuneId.fetch({
                codBelfiore: comuneId,
            });
            if(!capResponse.data.errors){
                if(capResponse.data.data.base_stdcodes_comune.length !== 0 && capResponse.data.data.base_stdcodes_comune[0].MultiCAP !== "S"){
                    return capResponse.data.data.base_stdcodes_comune[0].CAP
                }
            }
        } catch (error) {
            throw new Error("Error fetching CAP by comune ID");
        }
    }
    const GetComuneNomeByComuneId = async (comuneId) => {
        if (!comuneId || comuneId <= 0) return null;
        try {
            const comuneResponse = await apiBaseComuneById.fetch({
                id: comuneId,
            });
            if(!comuneResponse.data.errors){
                return comuneResponse.data.data.base_comune[0]?.Nome ?? null;
            }
        } catch (error) {
            throw new Error("Error fetching municipality name by comune ID");
        }
    }

    //#region Risoluzione toponimi provenienti da Workspace
    //A differenza dei GetIdBy* qui non si lancia mai: su miss si ritorna null, così un
    //singolo indirizzo non risolvibile non fa fallire l'intera importazione da Workspace.
    const TryGetPaeseByNome = async (nome) => {
        if (!nome) return null;
        try {
            const response = await countryByName.fetch({ nome: nome });
            return response.data.data.base_paese[0] ?? null;
        } catch (error) {
            return null;
        }
    }
    //Workspace manda i comuni in maiuscolo e senza punteggiatura: tento prima il match
    //esatto, poi uno con wildcard al posto dei separatori, così "CHALLAND SAINT ANSELME"
    //trova "Challand-Saint-Anselme".
    const TryGetComuneByNome = async (nome, provincia = null) => {
        if (!nome) return null;
        const normalizedNome = normalizeToponym(nome);
        if (!normalizedNome) return null;
        const filterProvincia = (comuni) =>
            provincia
                ? comuni.filter((comune) => normalizeToponym(comune.Provincia) === normalizeToponym(provincia))
                : comuni;
        try {
            let response = await minicipalityByName.fetch({ nome: nome });
            let comuni = filterProvincia(response.data.data.base_comune ?? []);
            if (comuni.length === 0) {
                response = await minicipalityByName.fetch({ nome: normalizedNome.replace(/ /g, "%") });
                comuni = filterProvincia(response.data.data.base_comune ?? []);
            }
            if (comuni.length === 0) return null;
            //La wildcard può restituire più righe (o esistono omonimi in province diverse):
            //preferisco quella il cui nome normalizzato coincide con quello cercato.
            return comuni.find((x) => normalizeToponym(x.Nome) === normalizedNome) ?? comuni[0];
        } catch (error) {
            return null;
        }
    }
    const TryGetProvinciaBySigla = async (sigla) => {
        if (!sigla) return null;
        try {
            const response = await provinceBySigla.fetch({ sigla: sigla });
            return response.data.data.base_provincia[0] ?? null;
        } catch (error) {
            return null;
        }
    }
    const TryGetProvinciaByNome = async (nome) => {
        if (!nome) return null;
        try {
            const response = await provinceByName.fetch({ nome: nome });
            return response.data.data.base_provincia[0] ?? null;
        } catch (error) {
            return null;
        }
    }
    const TryGetPaeseByIdentificativo = async (identificativo) => {
        if (!identificativo) return null;
        try {
            const response = await countryByIdent.fetch({ identificativo: identificativo });
            return response.data.data.base_paese[0] ?? null;
        } catch (error) {
            return null;
        }
    }
    const TryGetComuneByCodBelfiore = async (codBelfiore) => {
        if (!codBelfiore) return null;
        try {
            const response = await apiBaseComuneByCodBelfiore.fetch({ codBelfiore: codBelfiore });
            const comuni = response.data.data.base_comune ?? [];
            const comuneAttivo = comuni.find((comune) => !comune.DataSoppressione);
            if (comuneAttivo) return comuneAttivo;
            const comuneStorico = comuni[0];
            return comuneStorico
                ? await TryGetComuneByNome(comuneStorico.Nome, comuneStorico.Provincia)
                : null;
        } catch (error) {
            return null;
        }
    }
    //Workspace codifica gli indirizzi in due modi diversi a seconda dell'origine del
    //dato: per nome ("CHALLAND SAINT ANSELME", "AOSTA") oppure per codice ("A186", "SA").
    //Nessun comune italiano ha cifre nel nome e nessuna provincia ha un nome di due
    //lettere, quindi la forma è riconoscibile senza ambiguità.
    const isCodBelfiore = (value) => /^[a-z]\d{3}$/i.test((value ?? "").trim());
    const isSiglaProvincia = (value) => /^[a-z]{2}$/i.test((value ?? "").trim());
    const TryGetComuneByNomeOrCodBelfiore = async (value) =>
        isCodBelfiore(value) ? await TryGetComuneByCodBelfiore(value.trim()) : await TryGetComuneByNome(value);
    const TryGetProvinciaBySiglaOrNome = async (value) =>
        isSiglaProvincia(value) ? await TryGetProvinciaBySigla(value.trim().toUpperCase()) : await TryGetProvinciaByNome(value);
    //#endregion

    return {
        GetIdByPaese,
        GetIdByProvince,
        GetIdByMunicipality,
        GetMunicipalityByCodeBelfiore,
        GetPaeseByCodiceBelfiore,
        GetCapByComuneId,
        GetComuneNomeByComuneId,
        TryGetPaeseByNome,
        TryGetPaeseByIdentificativo,
        TryGetComuneByNome,
        TryGetComuneByCodBelfiore,
        TryGetComuneByNomeOrCodBelfiore,
        TryGetProvinciaBySigla,
        TryGetProvinciaByNome,
        TryGetProvinciaBySiglaOrNome,
        isCodBelfiore,
        isSiglaProvincia
    }
}

export default AddressApi;